import { agentErrorForFeedback } from "../../../lib/aiAgentSecrets";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  useAvailableProviders,
  useCreateAiAgent,
  useProviderSchema,
} from "../../../hooks/useAiAgents";
import { PROVIDER_FEATURE_MAP } from "../../../lib/subscriptionFeatures";
import { useBillingStore } from "../../../stores/billingStore";
import {
  useToast,
  useFranchisesInfinite,
  usePostHog,
} from "@finitless/shared/hooks";
import {
  useGlobalConfig,
  useIntercomContext,
} from "@finitless/shared/contexts";
import { handleApiError, handleApiSuccess } from "@finitless/shared/lib";
import { useTranslation } from "react-i18next";
import ProviderSettingsForm from "./ProviderSettingsForm";
import FranchiseSelect from "./FranchiseSelect";
import BranchSelectionModeField from "./BranchSelectionModeField";
import AgentWebviewColorsField from "./AgentWebviewColorsField";
import {
  AGENT_WEBVIEW_COLOR_DEFAULTS,
  buildWebviewColorsForCreate,
} from "../../../lib/aiAgentWebviewColors";
import { Modal, Switch } from "../../ui";
import { Button } from "../../ui/UIElements/Button";
import { NativeSelect } from "../../ui/UIElements/Select";
import { Icon } from "../../ui/UIElements/Icon";
import { useUiLocale } from "../../../hooks/useUiLocale";
import {
  resolveSchemaDescription,
  resolveSchemaTitle,
} from "../../../lib/aiAgentI18n";
import finitlessIsotype from "../../../assets/finitless-isotype.png";
import {
  normalizeSettingsLanguage,
  supportsManagedLanguage,
  LANGUAGE_OPTIONS,
  normalizeLanguageCode,
} from "../../../lib/aiAgentLanguage";
import {
  BRANCH_SELECTION_MODE_DEFAULT,
  buildBranchSelectionSettings,
} from "../../../lib/aiAgentBranchSelection";

const WhatsAppIcon = () => (
  <svg className="h-6 w-6 text-white" fill="currentColor" viewBox="0 0 24 24">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
  </svg>
);

const getProviderIcon = (provider) => {
  switch (provider) {
    case "whatsapp":
      return (
        <div className="ordering-provider-identity bg-success flex h-12 w-12 items-center justify-center rounded-lg">
          <WhatsAppIcon />
        </div>
      );
    case "finitless":
      return (
        <div className="ordering-provider-identity border-border bg-card flex h-12 w-12 items-center justify-center rounded-lg border">
          <img
            src={finitlessIsotype}
            alt="Finitless"
            className="h-8 w-8 object-contain"
          />
        </div>
      );
    default:
      return (
        <div className="ordering-provider-identity bg-muted flex h-12 w-12 items-center justify-center rounded-lg">
          <svg
            className="h-6 w-6 text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        </div>
      );
  }
};

const getProviderCardName = (provider, t) => {
  const key = `createModal.providerCards.${provider}.name`;
  const translated = t(key);
  // Fallback to capitalized provider name if key not found
  return translated !== key
    ? translated
    : provider.charAt(0).toUpperCase() + provider.slice(1);
};

const getProviderCardDescription = (provider, t) => {
  const key = `createModal.providerCards.${provider}.description`;
  const translated = t(key);
  return translated !== key
    ? translated
    : t("createModal.providerCards.default.description");
};

const CreateAgentModal = ({
  isOpen,
  onClose,
  onSuccess,
  onCreatedForOAuth,
}) => {
  const { t } = useTranslation("aiAgents");
  const { locale } = useUiLocale();
  const [selectedProvider, setSelectedProvider] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [providerSettings, setProviderSettings] = useState({});
  const [selectedFranchiseId, setSelectedFranchiseId] = useState("");
  const [isEnabled, setIsEnabled] = useState(true);
  const [branchSelectionMode, setBranchSelectionMode] = useState(
    BRANCH_SELECTION_MODE_DEFAULT,
  );
  const [webviewColors, setWebviewColors] = useState(
    AGENT_WEBVIEW_COLOR_DEFAULTS,
  );
  const navigate = useNavigate();

  const { data: availableProviders } = useAvailableProviders();
  const { isFeatureEnabled, isReady: isGlobalConfigReady } = useGlobalConfig();
  const setShowUpgradeModal = useBillingStore(
    (state) => state.setShowUpgradeModal,
  );
  const {
    franchises,
    isLoading: franchisesLoading,
    isFetchingNextPage: franchisesFetchingNextPage,
    hasNextPage: franchisesHasNextPage,
    fetchNextPage: fetchNextFranchisesPage,
  } = useFranchisesInfinite({ enabled: !!selectedProvider });

  const { data: schema } = useProviderSchema(selectedProvider, selectedType);
  const createAgentMutation = useCreateAiAgent();
  const toast = useToast();
  const { trackActivation } = usePostHog();
  const { showMessage, isEnabled: isIntercomEnabled } = useIntercomContext();

  // Check if a provider is enabled in the subscription
  const isProviderEnabled = (provider) => {
    const featureKey = PROVIDER_FEATURE_MAP[provider];
    if (!featureKey) return true; // No gate for unknown providers
    return isGlobalConfigReady && isFeatureEnabled(featureKey);
  };

  // Calculate required fields based on conditional schema (if/then/else)
  const getRequiredFields = () => {
    if (!schema) return [];

    const defaultAuthType = schema?.properties?.auth_type?.default || "manual";
    const authType = providerSettings?.auth_type || defaultAuthType;

    // Check for conditional required using if/then/else
    const ifCondition = schema.if;
    const thenRequired = schema.then?.required || [];
    const elseRequired = schema.else?.required || [];

    if (ifCondition?.properties) {
      const conditionField = Object.keys(ifCondition.properties)[0];
      const conditionValue = ifCondition.properties[conditionField]?.const;

      if (conditionField && conditionValue !== undefined) {
        return authType === conditionValue ? thenRequired : elseRequired;
      }
    }

    // Fallback to static required
    return schema.required || [];
  };

  // Validate all required fields before submission
  const validateRequiredFields = () => {
    const requiredFields = getRequiredFields();
    const missingFields = [];

    for (const field of requiredFields) {
      // Skip auth_type as it's always set
      if (field === "auth_type") continue;

      const value = providerSettings?.[field];
      const trimmedValue = typeof value === "string" ? value.trim() : value;

      if (!trimmedValue) {
        // Get field title from schema for better error message
        const fieldTitle = schema?.properties?.[field]?.title || field;
        missingFields.push(fieldTitle);
      }
    }

    return missingFields;
  };

  const getInitialSettings = (provider, schemaData) => {
    const base = supportsManagedLanguage(provider)
      ? normalizeSettingsLanguage(provider, {})
      : {};

    // Apply default values from schema
    if (schemaData?.properties) {
      for (const [key, prop] of Object.entries(schemaData.properties)) {
        if (prop.default !== undefined && base[key] === undefined) {
          base[key] = prop.default;
        }
      }
    }

    return base;
  };

  const handleProviderSelect = (provider) => {
    if (!isProviderEnabled(provider)) {
      handleClose();
      setShowUpgradeModal(true);
      return;
    }

    const providerData = availableProviders?.find(
      (p) => p.provider === provider,
    );
    const providerTypes = providerData?.types || [];
    const singleType = providerTypes.length === 1 ? providerTypes[0].type : "";

    setSelectedProvider(provider);
    setSelectedType(singleType);
    setProviderSettings(getInitialSettings(provider));
  };

  const handleBackToChannels = () => {
    setSelectedProvider("");
    setSelectedType("");
    setProviderSettings({});
    setSelectedFranchiseId("");
    setIsEnabled(true);
    setBranchSelectionMode(BRANCH_SELECTION_MODE_DEFAULT);
    setWebviewColors(AGENT_WEBVIEW_COLOR_DEFAULTS);
  };

  const handleTypeChange = (type) => {
    setSelectedType(type);
    setProviderSettings(getInitialSettings(selectedProvider));
  };

  const handleSettingsChange = (settings) => {
    setProviderSettings(
      supportsManagedLanguage(selectedProvider)
        ? normalizeSettingsLanguage(selectedProvider, settings)
        : settings,
    );
  };

  // Apply schema defaults when schema loads
  useEffect(() => {
    if (schema?.properties) {
      setProviderSettings((prev) => {
        const updated = { ...prev };
        let hasChanges = false;

        for (const [key, prop] of Object.entries(schema.properties)) {
          if (prop.default !== undefined && updated[key] === undefined) {
            updated[key] = prop.default;
            hasChanges = true;
          }
        }

        return hasChanges ? updated : prev;
      });
    }
  }, [schema]);

  const handleFranchiseSelect = (franchiseId) => {
    setSelectedFranchiseId(franchiseId);
  };

  // Check if using business_login auth type
  const isBusinessLogin = providerSettings?.auth_type === "business_login";
  const isWhatsApp = selectedProvider === "whatsapp";
  // Only the WhatsApp Bot (guided webview, no AI) paints these colors
  const isWebviewBot = isWhatsApp && selectedType === "chat_bot";
  const showLanguageInModal = isWhatsApp && isBusinessLogin;

  // Handle language code change
  const handleLanguageChange = (e) => {
    const normalizedValue = normalizeLanguageCode(e.target.value);
    setProviderSettings((prev) => ({
      ...prev,
      language_code: normalizedValue,
    }));
  };

  // Build agent data for creation
  const buildAgentData = () => {
    const cleanProviderSettings = supportsManagedLanguage(selectedProvider)
      ? normalizeSettingsLanguage(selectedProvider, providerSettings)
      : { ...(providerSettings || {}) };
    delete cleanProviderSettings.provider_external_id;

    const agentData = {
      provider: selectedProvider,
      provider_type: selectedType,
      provider_settings: cleanProviderSettings,
      franchise_id: parseInt(selectedFranchiseId),
      enabled: isEnabled,
      settings: {
        ...buildBranchSelectionSettings(branchSelectionMode),
        ...(isWebviewBot ? buildWebviewColorsForCreate(webviewColors) : {}),
      },
    };

    // For manual auth, include provider_external_id
    if (selectedProvider === "whatsapp" && !isBusinessLogin) {
      const rawExternalId = providerSettings?.provider_external_id;
      const trimmedExternalId =
        typeof rawExternalId === "string"
          ? rawExternalId.trim()
          : rawExternalId;
      if (trimmedExternalId) {
        agentData.provider_external_id = trimmedExternalId;
      }
    }

    return agentData;
  };

  // Create agent for OAuth flow - creates agent then opens edit modal for OAuth
  const handleCreateAgentForOAuth = async () => {
    if (!selectedProvider || !selectedType || !selectedFranchiseId) {
      toast.warning(t("createModal.validation.missingInformationMessage"), {
        title: t("createModal.validation.missingInformationTitle"),
      });
      return;
    }

    // For business_login, language_code is required
    if (!providerSettings?.language_code) {
      toast.warning(t("createModal.validation.missingLanguageMessage"), {
        title: t("createModal.validation.missingLanguageTitle"),
      });
      return;
    }

    const agentData = buildAgentData();

    try {
      const createdAgent = await createAgentMutation.mutateAsync(agentData);

      // Reset form
      setSelectedProvider("");
      setSelectedType("");
      setProviderSettings({});
      setSelectedFranchiseId("");
      setIsEnabled(true);
      setBranchSelectionMode(BRANCH_SELECTION_MODE_DEFAULT);
      setWebviewColors(AGENT_WEBVIEW_COLOR_DEFAULTS);

      // Open edit modal with auto-connect OAuth
      onCreatedForOAuth?.(createdAgent);
    } catch (error) {
      handleApiError(agentErrorForFeedback(error), toast, {
        title: t("createModal.toasts.createErrorTitle"),
        defaultMessage: t("createModal.toasts.createErrorMessage"),
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedProvider || !selectedType || !selectedFranchiseId) {
      toast.warning(t("createModal.validation.missingInformationMessage"), {
        title: t("createModal.validation.missingInformationTitle"),
      });
      return;
    }

    // For business_login, the OAuth flow handles creation
    if (selectedProvider === "whatsapp" && isBusinessLogin) {
      // User should click "Connect with WhatsApp Business" button instead
      return;
    }

    // Validate all required fields from schema
    const missingFields = validateRequiredFields();
    if (missingFields.length > 0) {
      toast.warning(
        t("createModal.validation.missingRequiredFieldsMessage", {
          fields: missingFields.join(", "),
        }),
        {
          title: t("createModal.validation.missingRequiredFieldsTitle"),
        },
      );
      return;
    }

    // For WhatsApp with manual auth, also require provider_external_id
    if (selectedProvider === "whatsapp" && !isBusinessLogin) {
      const rawExternalId = providerSettings?.provider_external_id;
      const trimmedExternalId =
        typeof rawExternalId === "string"
          ? rawExternalId.trim()
          : rawExternalId;
      if (!trimmedExternalId) {
        toast.warning(t("createModal.validation.missingExternalIdMessage"), {
          title: t("createModal.validation.missingExternalIdTitle"),
        });
        return;
      }
    }

    const agentData = buildAgentData();

    try {
      const createdAgent = await createAgentMutation.mutateAsync(agentData);

      // Track WhatsApp connection if it's a WhatsApp agent with manual auth
      const trimmedExternalId =
        providerSettings?.provider_external_id?.trim?.();
      if (
        selectedProvider === "whatsapp" &&
        trimmedExternalId &&
        createdAgent?.id
      ) {
        trackActivation.whatsappConnected({
          agent_id: createdAgent.id,
          provider_external_id: trimmedExternalId,
          franchise_id: parseInt(selectedFranchiseId),
        });
      }

      handleApiSuccess(t("createModal.toasts.createSuccess"), toast, {
        title: t("common.success"),
      });
      onSuccess?.();
      onClose();
      // Reset form
      setSelectedProvider("");
      setSelectedType("");
      setProviderSettings({});
      setSelectedFranchiseId("");
      setIsEnabled(true);
      setBranchSelectionMode(BRANCH_SELECTION_MODE_DEFAULT);
      setWebviewColors(AGENT_WEBVIEW_COLOR_DEFAULTS);
    } catch (error) {
      handleApiError(agentErrorForFeedback(error), toast, {
        title: t("createModal.toasts.createErrorTitle"),
        defaultMessage: t("createModal.toasts.createErrorMessage"),
      });
    }
  };

  const handleClose = () => {
    setSelectedProvider("");
    setSelectedType("");
    setProviderSettings({});
    setSelectedFranchiseId("");
    setIsEnabled(true);
    setBranchSelectionMode(BRANCH_SELECTION_MODE_DEFAULT);
    setWebviewColors(AGENT_WEBVIEW_COLOR_DEFAULTS);
    onClose();
  };

  const selectedProviderData = availableProviders?.find(
    (p) => p.provider === selectedProvider,
  );
  const shouldShowTypeSelect =
    selectedProvider && (selectedProviderData?.types?.length || 0) > 1;

  const handleGoToBrands = () => {
    handleClose();
    navigate("/menu/brands?create=true");
  };

  // Show channel selection cards when no provider is selected
  const showChannelSelection = !selectedProvider;

  const showNoFranchisesEmptyState =
    !franchisesLoading && franchises.length === 0;

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title={t("createModal.title")}
        subtitle={t("createModal.subtitle")}
        maxWidth="max-w-2xl"
      >
        {showChannelSelection ? (
          /* Channel Selection Cards */
          <div className="ordering-ai-agent-modal-content space-y-4">
            <p className="text-muted-foreground text-sm font-medium">
              {t("createModal.labels.provider")}
            </p>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {availableProviders?.map((provider) => {
                const enabled = isProviderEnabled(provider.provider);
                return (
                  <Button
                    key={provider.provider}
                    type="button"
                    variant="outline"
                    size="touch"
                    onClick={() => handleProviderSelect(provider.provider)}
                    className={`relative h-auto w-full items-start justify-start gap-4 rounded-lg p-4 text-start whitespace-normal ${
                      enabled
                        ? "border-border bg-background hover:border-primary hover:bg-accent"
                        : "border-border bg-muted hover:border-primary"
                    }`}
                    data-testid={`provider-card-${provider.provider}`}
                  >
                    {/* Provider icon */}
                    <div
                      className={`flex-shrink-0 ${!enabled ? "opacity-50" : ""}`}
                    >
                      {getProviderIcon(provider.provider)}
                    </div>

                    {/* Provider info */}
                    <div className="min-w-0 flex-1 pt-0.5">
                      <h4
                        className={`text-sm font-semibold ${enabled ? "text-foreground" : "text-muted-foreground"}`}
                      >
                        {getProviderCardName(provider.provider, t)}
                      </h4>
                      <p
                        className={`mt-1 text-xs leading-relaxed ${enabled ? "text-muted-foreground" : "text-muted-foreground"}`}
                      >
                        {getProviderCardDescription(provider.provider, t)}
                      </p>
                      {!enabled && (
                        <div className="bg-primary text-primary-foreground mt-3 inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold">
                          <Icon name="Zap" className="h-3 w-3" />
                          {t("createModal.providerCards.upgrade")}
                        </div>
                      )}
                    </div>
                  </Button>
                );
              })}
            </div>

            {/* Cancel button */}
            <div className="border-border flex border-t pt-4">
              <Button
                type="button"
                emphasis="outline"
                intent="neutral"
                size="touch"
                onClick={handleClose}
                className="flex-1"
              >
                {t("actions.cancel")}
              </Button>
            </div>
          </div>
        ) : (
          /* Agent Configuration Form */
          <form
            onSubmit={handleSubmit}
            className="ordering-ai-agent-modal-content space-y-6"
          >
            {/* Back to channel selection */}
            <Button
              type="button"
              emphasis="ghost"
              intent="neutral"
              size="touch"
              onClick={handleBackToChannels}
              className="px-0"
            >
              <Icon name="ArrowLeft" className="h-4 w-4" />
              {t("createModal.providerCards.backToChannels")}
            </Button>

            {/* Selected provider indicator */}
            <div className="border-border bg-card/50 flex items-center gap-3 rounded-lg border p-3">
              {getProviderIcon(selectedProvider)}
              <div>
                <h4 className="text-foreground text-sm font-semibold">
                  {getProviderCardName(selectedProvider, t)}
                </h4>
                <p className="text-muted-foreground text-xs">
                  {getProviderCardDescription(selectedProvider, t)}
                </p>
              </div>
            </div>

            {/* Type Selection */}
            {shouldShowTypeSelect && (
              <div>
                <label
                  htmlFor="create-agent-type"
                  className="text-muted-foreground mb-2 block text-[length:var(--font-size-product-ui)] font-medium"
                >
                  {t("createModal.labels.type")}
                </label>
                <NativeSelect
                  id="create-agent-type"
                  value={selectedType}
                  onChange={(e) => handleTypeChange(e.target.value)}
                  className="w-full"
                  required
                >
                  <option value="">
                    {t("createModal.placeholders.selectType")}
                  </option>
                  {selectedProviderData?.types?.map((type) => (
                    <option key={type.type} value={type.type}>
                      {resolveSchemaTitle({
                        title: type.title,
                        provider: selectedProvider,
                        type: type.type,
                        t,
                        locale,
                      })}
                    </option>
                  ))}
                </NativeSelect>
                {selectedProviderData?.types?.find(
                  (type) => type.type === selectedType,
                )?.description ? (
                  <p className="text-muted-foreground mt-2 text-sm">
                    {resolveSchemaDescription({
                      description: selectedProviderData.types.find(
                        (type) => type.type === selectedType,
                      ).description,
                      provider: selectedProvider,
                      type: selectedType,
                      t,
                      locale,
                    })}
                  </p>
                ) : null}
              </div>
            )}

            {/* WhatsApp Bot only: brand colors of the guided webview */}
            {isWebviewBot && (
              <AgentWebviewColorsField
                value={webviewColors}
                onChange={setWebviewColors}
              />
            )}

            {/* Brand, Enable Agent, and Language Code (for WhatsApp business_login) */}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label
                  htmlFor="create-agent-franchise-select"
                  className="text-muted-foreground mb-2 block text-[length:var(--font-size-product-ui)] font-medium"
                >
                  {t("createModal.labels.franchise")}
                </label>
                {showNoFranchisesEmptyState ? (
                  <div className="space-y-3">
                    <div className="border-border bg-card flex items-center justify-center rounded-md border p-4">
                      <div className="text-center">
                        <Icon
                          name="AlertCircle"
                          className="text-warning mx-auto mb-2 h-8 w-8"
                        />
                        <p className="text-muted-foreground mb-3 text-sm">
                          {t("createModal.empty.noFranchises")}
                        </p>
                        <Button
                          type="button"
                          emphasis="solid"
                          intent="brand"
                          size="touch"
                          onClick={handleGoToBrands}
                        >
                          <Icon name="Plus" className="h-4 w-4" />
                          <span>{t("createModal.actions.goToBrands")}</span>
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <FranchiseSelect
                    id="create-agent-franchise-select"
                    value={selectedFranchiseId}
                    onChange={handleFranchiseSelect}
                    disabled={franchisesLoading}
                    placeholder={t("createModal.placeholders.selectFranchise")}
                    placeholderLoading={t(
                      "createModal.placeholders.loadingFranchises",
                    )}
                    loadingMoreLabel={t(
                      "createModal.placeholders.loadingMoreFranchises",
                    )}
                    franchises={franchises}
                    isLoading={franchisesLoading}
                    isFetchingNextPage={franchisesFetchingNextPage}
                    hasNextPage={franchisesHasNextPage}
                    fetchNextPage={fetchNextFranchisesPage}
                    disabledSuffix={t("common.disabledSuffix")}
                    required
                    aria-label={t("createModal.labels.franchise")}
                  />
                )}
              </div>
              <div>
                <label
                  htmlFor="enabled"
                  className="text-muted-foreground mb-2 block text-[length:var(--font-size-product-ui)] font-medium"
                >
                  {t("createModal.labels.enableAgent")}
                </label>
                <div className="border-border bg-card flex h-10 items-center rounded-md border px-3 py-2">
                  <Switch
                    id="enabled"
                    checked={isEnabled}
                    onCheckedChange={setIsEnabled}
                  />
                </div>
              </div>

              {/* Language Code - shown in modal for WhatsApp business_login */}
              {showLanguageInModal && (
                <div className="md:col-span-2">
                  <label
                    htmlFor="create-agent-language"
                    className="text-muted-foreground mb-2 block text-[length:var(--font-size-product-ui)] font-medium"
                  >
                    {t("settingsForm.languageTitle")}
                    <span aria-hidden="true" className="text-destructive ms-1">
                      *
                    </span>
                  </label>
                  <NativeSelect
                    id="create-agent-language"
                    value={normalizeLanguageCode(
                      providerSettings?.language_code || "",
                    )}
                    onChange={handleLanguageChange}
                    className="w-full"
                    required
                  >
                    {LANGUAGE_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {t(`settingsForm.languageOptions.${option.value}`)}
                      </option>
                    ))}
                  </NativeSelect>
                  {isIntercomEnabled && (
                    <Button
                      type="button"
                      emphasis="link"
                      intent="brand"
                      size="sm"
                      onClick={() =>
                        showMessage(
                          t("settingsForm.newLanguageIntercomPrefill"),
                        )
                      }
                      className="mt-1 h-auto px-0"
                    >
                      {t("settingsForm.newLanguageCta")}
                    </Button>
                  )}
                </div>
              )}

              {/* Branch selection mode */}
              <div className="md:col-span-2">
                <BranchSelectionModeField
                  value={branchSelectionMode}
                  onChange={setBranchSelectionMode}
                />
              </div>
            </div>

            {/* Provider Settings Form */}
            {selectedProvider && selectedType && (
              <div>
                <ProviderSettingsForm
                  provider={selectedProvider}
                  type={selectedType}
                  initialValues={providerSettings}
                  onChange={handleSettingsChange}
                  readOnly={false}
                  onCreateAgent={handleCreateAgentForOAuth}
                />
              </div>
            )}

            {/* Action Buttons */}
            <div className="border-border flex gap-3 border-t pt-6 pb-24 sm:pb-0">
              <Button
                type="button"
                emphasis="outline"
                intent="neutral"
                size="touch"
                onClick={handleClose}
                className="flex-1"
              >
                {t("actions.cancel")}
              </Button>
              <Button
                type="submit"
                emphasis="solid"
                intent="brand"
                size="touch"
                disabled={
                  createAgentMutation.isPending ||
                  !selectedProvider ||
                  !selectedType ||
                  !selectedFranchiseId
                }
                className="flex-1"
              >
                {createAgentMutation.isPending
                  ? t("createModal.actions.creating")
                  : t("createModal.actions.createAgent")}
              </Button>
            </div>
          </form>
        )}
      </Modal>
    </>
  );
};

export default CreateAgentModal;
